// AanvraagDetailPanel.jsx — detail overlay voor één aanvraag
import { useLanguage } from '../../LanguageContext';
import React from 'react';
import { formatPrijs, formatDate } from './aanvragenModal.constants';

const AanvraagDetailPanel = ({
  detailAanvraag,
  detailPos,
  setDetailPos,
  detailTrackerNotes,
  setDetailAanvraag,
  setShowFactuurPdf,
  onOpenBordero,
}) => {
  const { t } = useLanguage();
  if (!detailAanvraag) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-10" style={{ zIndex: 10000 }}>
      <div
        className="bg-white shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-auto" style={{ border:"1px solid #d1dce6", borderRadius:8 }}
        style={{ position: 'absolute', left: detailPos.x + 'px', top: detailPos.y + 'px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 text-white p-4 flex justify-between items-center cursor-move select-none" style={{ background:"linear-gradient(135deg,#1a364c 0%,#24496a 100%)", borderRadius:"8px 8px 0 0" }}
          onMouseDown={(e) => {
            const startX = e.clientX - detailPos.x;
            const startY = e.clientY - detailPos.y;
            const onMove = (ev) => setDetailPos({ x: ev.clientX - startX, y: ev.clientY - startY });
            const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}
        >
          <h3 className="text-xl font-bold">Aanvraag #{detailAanvraag.AanvraagID}</h3>
          <button onClick={() => setDetailAanvraag(null)} className="w-8 h-8 flex items-center justify-center transition-colors text-xl" style={{ background:"rgba(255,255,255,.15)", borderRadius:"50%" }}>&times;</button>
        </div>
        <div style={{ height:3, background:"linear-gradient(90deg,#d4a848,#f0c96a)", flexShrink:0 }} />

        <div className="p-5 space-y-4">
          {/* Status */}
          <div className="p-3 flex items-center justify-between" style={{ background:"#f0f6fa", borderLeft:"4px solid #1a364c", borderRadius:4 }}>
            <span className="font-bold text-sm" style={{ color:"#1a364c" }}>{t('status').toUpperCase()}</span>
            <span className="font-bold" style={{ color:"#1a364c" }}>{detailAanvraag.KlantStatus || detailAanvraag.Status || '-'}</span>
          </div>

          {/* Route */}
          <div className="overflow-hidden" style={{ border:"1px solid #d1dce6", borderRadius:6 }}>
            <div className="px-4 py-2 font-semibold text-sm" style={{ background:"#1a364c", color:"white", letterSpacing:".05em" }}>{t('route')}</div>
            <div className="grid grid-cols-2 gap-0 divide-x divide-gray-200">
              {[
                { label: 'Laadplaats', plaats: detailAanvraag.LaadPlaats, adres: detailAanvraag.LaadAdres, postcode: detailAanvraag.LaadPostcode, land: detailAanvraag.LaadLand, datum: detailAanvraag.LaadDatum, bijz: detailAanvraag.LaadBijzonderheden },
                { label: 'Losplaats',  plaats: detailAanvraag.LosPlaats,  adres: detailAanvraag.LosAdres,  postcode: detailAanvraag.LosPostcode,  land: detailAanvraag.LosLand,  datum: detailAanvraag.LosDatum,  bijz: detailAanvraag.LosBijzonderheden },
              ].map(r => (
                <div key={r.label} className="p-4">
                  <div className="text-xs font-semibold text-gray-400 uppercase mb-1">{r.label}</div>
                  <div className="font-bold text-gray-900 text-lg">{r.plaats || '-'}</div>
                  <div className="text-sm text-gray-600">{r.adres || ''}</div>
                  <div className="text-sm text-gray-600">{r.postcode || ''} {r.land || ''}</div>
                  {r.datum && <div className="text-sm text-blue-700 mt-2 font-medium">Datum: {formatDate(r.datum)}</div>}
                  {r.bijz && <div className="text-sm text-orange-600 mt-1 italic border-l-2 border-orange-400 pl-2">{r.bijz}</div>}
                </div>
              ))}
            </div>
          </div>

          {/* Goederen */}
          <div className="overflow-hidden" style={{ border:"1px solid #d1dce6", borderRadius:6 }}>
            <div className="px-4 py-2 font-semibold text-sm" style={{ background:"#1a364c", color:"white", letterSpacing:".05em" }}>{t('goederen')}</div>
            <div className="p-3">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-blue-900">
                    {['Aantal','Soort','Omschrijving','Afmetingen','Gewicht','Stack'].map(h => (
                      <th key={h} className="text-left py-2 px-1 font-semibold text-sm" style={{ color:"#1a364c", borderBottom:"2px solid #1a364c" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const rows = [];
                    for (let i = 1; i <= 5; i++) {
                      const soort = detailAanvraag['Goed' + i + '_Soort'];
                      const omschrijving = detailAanvraag['Goed' + i + '_Omschrijving'];
                      if (!soort && !omschrijving) continue;
                      rows.push(
                        <tr key={i} className="border-t border-gray-200">
                          <td className="py-2 px-1">{detailAanvraag['Goed' + i + '_Aantal'] || 1}</td>
                          <td className="py-2 px-1 font-medium">{soort || '-'}</td>
                          <td className="py-2 px-1">{omschrijving || '-'}</td>
                          <td className="py-2 px-1">
                            {detailAanvraag['Goed' + i + '_Lengte'] && detailAanvraag['Goed' + i + '_Breedte'] && detailAanvraag['Goed' + i + '_Hoogte']
                              ? `${detailAanvraag['Goed' + i + '_Lengte']} x ${detailAanvraag['Goed' + i + '_Breedte']} x ${detailAanvraag['Goed' + i + '_Hoogte']} m`
                              : '-'}
                          </td>
                          <td className="py-2 px-1">{detailAanvraag['Goed' + i + '_Gewicht'] ? detailAanvraag['Goed' + i + '_Gewicht'] + ' kg' : '-'}</td>
                          <td className="py-2 px-1">{detailAanvraag['Goed' + i + '_Stackable'] === 'J' ? 'Ja' : 'Nee'}</td>
                        </tr>
                      );
                    }
                    return rows.length > 0 ? rows : (
                      <tr><td colSpan="6" className="py-3 text-gray-400 italic text-center">{t('geen_goederen')}</td></tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </div>

          {/* Specialiteiten */}
          {(() => {
            const services = ['Express','Klep','ADR','Kooiaap','Parcel','Groupage','LTL','FTL','Customs','LastMile','Geconditioneerd']
              .filter(s => detailAanvraag[s] === 'J');
            if (services.length === 0) return null;
            return (
              <div className="overflow-hidden" style={{ border:"1px solid #d1dce6", borderRadius:6 }}>
                <div className="px-4 py-2 font-semibold text-sm" style={{ background:"#1a364c", color:"white", letterSpacing:".05em" }}>{t('specialiteiten')}</div>
                <div className="p-3 flex flex-wrap gap-2">
                  {services.map(s => <span key={s} className="px-3 py-1 text-sm font-medium" style={{ background:"#e8f4f8", color:"#1a364c", border:"1px solid #c2e6ef", borderRadius:4 }}>{s}</span>)}
                </div>
              </div>
            );
          })()}

          {/* Prijs */}
          {detailAanvraag.KlantPrijs && (
            <div className="overflow-hidden" style={{ border:"1px solid #d4a848", borderRadius:6 }}>
              <div className="px-4 py-2 font-semibold text-sm" style={{ background:"#d4a848", color:"#1a364c", letterSpacing:".05em" }}>{t('prijs_excl_btw')}</div>
              <div className="p-5 text-center bg-green-50">
                <div className="text-4xl font-bold" style={{ color:"#1a364c" }}>&#8364; {formatPrijs(detailAanvraag.KlantPrijs)}</div>
              </div>
            </div>
          )}

          {/* Tracker */}
          {detailAanvraag.TrackerID && (
            <div className="overflow-hidden" style={{ border:"1px solid #d1dce6", borderRadius:6 }}>
              <div className="px-4 py-2 font-semibold text-sm" style={{ background:"#1a364c", color:"white", letterSpacing:".05em" }}>{t('tracker')}</div>
              <div className="p-4">
                <div className="p-4 text-center" style={{ background:"#f0f6fa", border:"1px solid #c2e6ef", borderRadius:4 }}>
                  <div className="text-xs text-gray-500 mb-1">Plaats deze tracker op de zending:</div>
                  <span className="text-2xl font-bold tracking-widest" style={{ color:"#1a364c" }}>
                    {detailTrackerNotes ? detailTrackerNotes.toUpperCase() : detailAanvraag.TrackerID}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Factuur */}
          {detailAanvraag.FactuurNr && (
            <div className="overflow-hidden" style={{ border:"1px solid #d1dce6", borderRadius:6 }}>
              <div className="px-4 py-2 font-semibold text-sm" style={{ background:"#1a364c", color:"white", letterSpacing:".05em" }}>{t('factuur')}</div>
              <div className="p-4 flex items-center justify-between">
                <span className="text-sm text-gray-500">{t('factuurnummer')}:</span>
                <span
                  className="font-bold text-lg cursor-pointer hover:underline" style={{ color:"#1a364c" }}
                  onClick={() => setShowFactuurPdf(detailAanvraag.FactuurNr)}
                  title="Klik om factuur te bekijken"
                >
                  {detailAanvraag.FactuurNr}
                </span>
              </div>
            </div>
          )}

          {/* Zendingdossier knop */}
          {onOpenBordero && (
            <div style={{ marginTop:8 }}>
              <button
                onClick={() => { onOpenBordero(detailAanvraag); setDetailAanvraag(null); }}
                style={{
                  width:'100%', padding:'10px 16px',
                  background:'#1a364c', color:'white',
                  border:'none', borderRadius:6, cursor:'pointer',
                  fontSize:13, fontWeight:600, display:'flex',
                  alignItems:'center', justifyContent:'center', gap:8,
                }}
              >
                📋 Zendingdossier bekijken
              </button>
            </div>
          )}

          {/* Opmerkingen */}
          {detailAanvraag.Opmerkingen && (
            <div className="overflow-hidden" style={{ border:"1px solid #d1dce6", borderRadius:6 }}>
              <div className="px-4 py-2 font-semibold text-sm" style={{ background:"#1a364c", color:"white", letterSpacing:".05em" }}>{t('opmerkingen')}</div>
              <div className="p-4 text-sm text-gray-700">{detailAanvraag.Opmerkingen}</div>
            </div>
          )}

          {/* Overzicht */}
          <div className="overflow-hidden" style={{ border:"1px solid #d1dce6", borderRadius:6 }}>
            <div className="px-4 py-2 font-semibold text-sm" style={{ background:"#1a364c", color:"white", letterSpacing:".05em" }}>{t('overzicht')}</div>
            <div className="p-4 space-y-2 text-sm">
              <div className="flex justify-between border-b border-gray-100 pb-1"><span className="text-gray-500">{t('aanvraagdatum')}:</span><span className="font-medium">{formatDate(detailAanvraag.Datum)}</span></div>
              <div className="flex justify-between border-b border-gray-100 pb-1"><span className="text-gray-500">Status:</span><span className="font-bold" style={{ color:"#1a364c" }}>{detailAanvraag.KlantStatus || detailAanvraag.Status || '-'}</span></div>
              {detailAanvraag.KlantBevestigdOp && <div className="flex justify-between border-b border-gray-100 pb-1"><span className="text-gray-500">Akkoord gegeven:</span><span className="font-medium" style={{ color:"#059669" }}>{formatDate(detailAanvraag.KlantBevestigdOp)}</span></div>}
              {detailAanvraag.Referentie1 && <div className="flex justify-between border-b border-gray-100 pb-1"><span className="text-gray-500">Referentie 1:</span><span className="font-medium">{detailAanvraag.Referentie1}</span></div>}
              {detailAanvraag.Referentie2 && <div className="flex justify-between"><span className="text-gray-500">Referentie 2:</span><span className="font-medium">{detailAanvraag.Referentie2}</span></div>}
            </div>
          </div>

          {/* Annuleringsreden */}
          {detailAanvraag.AfschrijfReden && (
            <div className="overflow-hidden" style={{ border:"1px solid #fca5a5", borderRadius:6 }}>
              <div className="px-4 py-2 font-semibold text-sm" style={{ background:"#dc2626", color:"white", letterSpacing:".05em" }}>Geannuleerd</div>
              <div className="p-4">
                <div className="flex justify-between mb-1"><span className="text-gray-500 text-sm">Reden:</span><span className="font-semibold" style={{ color:"#dc2626" }}>{detailAanvraag.AfschrijfReden}</span></div>
                {detailAanvraag.AfschrijfDatum && <div className="flex justify-between"><span className="text-gray-500 text-sm">Datum:</span><span className="font-medium">{detailAanvraag.AfschrijfDatum}</span></div>}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 p-4 flex justify-end" style={{ background:"#f7f6f2", borderTop:"1px solid #d1dce6" }}>
          <button onClick={() => setDetailAanvraag(null)} className="px-8 py-2.5 text-white font-semibold transition-colors" style={{ background:"#1a364c", borderRadius:6 }}>
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
};

export default AanvraagDetailPanel;
